import React from 'react';
import SummaryCard from 'src/shared/components/card-templates/SummaryCard';
import ExploreButton from 'src/shared/components/explore-button/ExploreButton';
import IucnSpeciesChart from 'src/shared/components/iucn-species-chart/IucnSpeciesChart';
import InvasiveSpeciesIcon from 'src/shared/components/invasive-species-icon/InvasiveSpeciesIcon';
import IucnLogo from 'src/shared/components/iucn-logo';
import SiteSpeciesRiskModal from 'src/app/portfolio/views/portfolio-site-explorer/components/site-species-risk-modal/SiteSpeciesRiskModal';
import { SiteSummaryData } from '../state/api/siteSummaryQuery';
import useSiteSummaryAnalytics from '../hooks/useSiteSummaryAnalytics';
import styles from '../SiteSummary.module.scss';

type Props = {
    site: SiteSummaryData;
};

const SiteSpeciesRiskIndicators = ({ site }: Props) => {
    const [modalState, setModalState] = React.useState<null | { tab: 'iucn' | 'invasives' }>(null);
    const { trackExploreButtonClick } = useSiteSummaryAnalytics();

    const totalPredictedInvasiveSpecies = site.predictedSpeciesSummary.invasiveSpecies.count;
    const totalPredictedRedListSpecies = site.predictedSpeciesSummary.redListSpecies.count;

    const handleExploreButtonClick = (section: string, tab: 'iucn' | 'invasives') => {
        trackExploreButtonClick({ section: section });
        setModalState({ tab: tab });
    };

    return (
        <div className='flex flex-col gap-3'>
            <div className={`text-xl text-black ${styles.sectionTitle}`}>Species risk indicators</div>
            <div className='flex gap-3 items-stretch'>
                <div className='w-1/4'>
                    <SummaryCard
                        containerClasses={`gap-3 p-5 h-full ${styles.invasiveSpeciesCard}`}
                        cardIcon={
                            <div className={styles.iconGradient}>
                                <InvasiveSpeciesIcon />
                            </div>
                        }
                        cardTitle='Invasive Species'
                        cardSubtitle='Predicted invasive species overlap across site'
                        cardBody={
                            <div className='flex flex-col gap-1'>
                                <div className='text-[42px] text-grey-80'>{totalPredictedInvasiveSpecies ?? 0}</div>
                                <div className='text-sm text-grey-100'>Total invasive species</div>
                            </div>
                        }
                        action={
                            <ExploreButton
                                sectionName='invasive-species'
                                onClick={() => handleExploreButtonClick('species_risk_invasive_species', 'invasives')}
                            />
                        }
                    />
                </div>

                <div className='w-3/4'>
                    <SummaryCard
                        containerClasses={`gap-3 p-5 h-full ${styles.iucnCard}`}
                        cardIcon={
                            <div className={styles.iconGradient}>
                                <IucnLogo />
                            </div>
                        }
                        cardTitle='IUCN Red List species'
                        cardSubtitle='Predicted red list species overlap across site'
                        cardBody={
                            <div className='flex gap-4'>
                                <div className='flex flex-col'>
                                    <div className='flex gap-2 items-end'>
                                        <span className='text-[42px] text-grey-80'>{totalPredictedRedListSpecies ?? 0}</span>
                                    </div>
                                    <div className='text-sm text-grey-80'>Total red list species</div>
                                </div>

                                <div className='bg-grey-20 w-[1px]'>&nbsp;</div>

                                <IucnSpeciesChart className='flex' chartData={site.predictedSpeciesSummary.redListSpecies.chartData} />
                            </div>
                        }
                        action={
                            <ExploreButton
                                sectionName='iucn-red-list-species'
                                onClick={() => handleExploreButtonClick('species_risk_iucn_red_list_species', 'iucn')}
                            />
                        }
                    />
                </div>
            </div>
            {modalState && <SiteSpeciesRiskModal siteId={site.id} initialTab={modalState.tab} onClose={() => setModalState(null)} />}
        </div>
    );
};

export default SiteSpeciesRiskIndicators;
